import Raven from 'raven-js';
import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { useCallback, useRef } from 'react';
const logUnmatchedFeInteraction = (functionName, interactions) => {
  const errorMessage = `Unmatched feInteraction: functionName "${functionName}" not found in registered actions`;
  const registeredActions = interactions === null || interactions === void 0 ? void 0 : interactions.map(a => a.name);
  consoleIfNotProd.error(errorMessage, {
    functionName,
    registeredActions
  });
  Raven.captureMessage(errorMessage, {
    level: 'error',
    extra: {
      functionName,
      registeredActions
    }
  });
};
import { MAX_DEPTH, getExtractedParameterNames } from './jsonSchemaUtils';
export const useFunctionQueue = appName => {
  const queue = useRef([]);
  const isProcessing = useRef(false);
  const processQueue = useCallback(async usageTracker => {
    if (isProcessing.current || queue.current.length === 0) return;
    isProcessing.current = true;
    const {
      function: fn,
      functionParameters,
      name,
      asyncTimeout = 2000,
      onResult
    } = queue.current[0];
    try {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Function ${name} execution timed out after ${asyncTimeout}ms`));
        }, asyncTimeout);
        Promise.resolve(fn(functionParameters)).then(response => {
          if (onResult) {
            onResult(response);
          }
          if (usageTracker) {
            usageTracker.track('interaction', {
              action: 'interactive_mode_auto_execute',
              hostAppName: appName,
              interactiveModeFunctionName: name
            });
          }
          clearTimeout(timeout);
          resolve(String(response));
        }).catch(error => {
          reject(error);
          Raven.captureException(error, {
            extra: {
              message: `Function ${name} execution failed`,
              component: 'useFunctionQueue',
              functionName: name
            }
          });
        });
      });
    } catch (error) {
      Raven.captureException(error, {
        extra: {
          message: `Function ${name} execution timed out after ${asyncTimeout}ms`,
          component: 'useFunctionQueue',
          functionName: name
        }
      });
    }
    queue.current.shift();
    isProcessing.current = false;
    void processQueue();
  }, [appName]);
  const addToQueue = useCallback(({
    function: fn,
    functionParameters,
    name,
    asyncTimeout = 2000,
    usageTracker,
    onResult
  }) => {
    queue.current.push({
      function: fn,
      functionParameters,
      name,
      asyncTimeout,
      onResult
    });
    void processQueue(usageTracker);
  }, [processQueue]);
  return {
    addToQueue
  };
};

// Used with v2.  Everything above should be deleted.
export const formatContextInstructions = actions => {
  const actionsAsMarkDown = actions.filter(action => !action.toolOutputOnly).map(action => {
    const actionName = `## ${action.name}`;
    const formatValue = (keyPath, value, depth) => {
      if (depth >= MAX_DEPTH) {
        return [`current ${keyPath}: ${JSON.stringify(value)}`];
      }
      if (Array.isArray(value)) {
        // Iterate through array items with index notation
        return value.flatMap((item, index) => formatValue(`${keyPath}[${index}]`, item, depth + 1));
      }
      if (value !== null && typeof value === 'object') {
        // Recursively handle object properties
        return Object.entries(value).flatMap(([childKey, childVal]) => formatValue(`${keyPath}.${childKey}`, childVal, depth + 1));
      }

      // Primitive value or null
      return [`current ${keyPath}: ${JSON.stringify(value)}`];
    };
    const parameterLines = [];
    Object.entries(action.functionParameters).forEach(([paramName, param]) => {
      parameterLines.push(...formatValue(paramName, param.currentValue, 0));
    });
    const parameters = parameterLines.join('\n');
    return parameters ? `${actionName}\n${parameters}` : actionName;
  }).join('\n');
  if (!actionsAsMarkDown) {
    return undefined;
  }
  return `# You may take actions\n${actionsAsMarkDown}`;
};

// // TODO: figure out character limit for external team - and throw error if too long.

export const formatResponseInstructions = additionalLLMInstructions => `<response-guidelines>

<fe-interactions-guidelines>
- ***CRITICAL***: FE interactions are functions for the frontend app to use and should not be executed as tools by the LLM in any scenario as they are not tools.
- FE interactions should be returned in the feInteractionsToExecute array from the LLM response.
- Only return FE interactions when all required parameters are known and unambiguous (either explicitly provided by the user or clearly inferable from context).
- ***CRITICAL***: If you cannot confidently match the user’s request to a FE interaction, or if any required parameters are missing or ambiguous:
  - Return \`feInteractionsToExecute: []\`
  - Ask the user a clear follow-up question to obtain the missing information or clarify intent.
- Do not make assumptions about parameter values.
- Example prompts that require clarification (return empty + ask):
  - "Create a new deal"
  - "Update the executive summary"
  - "Create a new contact"
  - "Update the contact"
  - "Update date and add it to a section"
</fe-interactions-guidelines>

<communication>
  - IMPORTANT: When sending a reply that includes feInteractionsToExecute, don't say that the interactions have been applied or that something has changed (no past tense).  Also don't say it will happen immediately.  If you say something about the changes happening, use future tense without a timeframe.
  - Present multiple options or alternatives as HTML ordered or unordered lists when appropriate.
</communication>

<button-behavior>
  - When the user makes a direct change request (e.g., "update the executive summary"), in that case showAsButton should be set to false.
  - Set **showAsButton** to true for suggestions, improvements, or unclear commands that need confirmation
  - Set **showAsButton** to false for direct commands where the action can be performed immediately
</button-behavior>

<button-field-requirements>
<important>IMPORTANT: GENERATE ALL 9 FIELDS REQUIRED regardless of showAsButton value</important>
  <applied-state-fields>
    <context>Shown when Undo button is displayed - action is currently applied</context>
    - **buttonText**: Text for Undo button (5-30 characters). Examples: "Undo changes", "Revert update"
    - **buttonExplanation**: Description of what the interaction did when it was applied (50-100 characters). Past tense. Example: "Updated the executive summary to be more concise". DO NOT say that the change was reverted, this is for when it was applied.
    - **buttonHeader**: Short summary of what the interaction did when it was applied (10-25 characters). Past tense. Example: "Summary updated". DO NOT say that the change was reverted, this is for when it was applied.
    - buttonExplanation and buttonHeader should provide a consistent message about what was applied, and buttonText should provide an action for what can be done next.
  </applied-state-fields>

  <pending-state-fields>
    <context>Shown when Apply button is displayed - action has never been applied</context>
    - **pendingButtonText**: Text for Apply button (5-30 characters). Examples: "Apply changes", "Update section"
    - **pendingButtonExplanation**: Description of what running the interaction will do (50-100 characters). Future tense. Example: "Will update the executive summary to be more concise"
    - **pendingButtonHeader**: Short summary of what running the interaction will do (10-25 characters). Future tense. Example: "Update summary"
    - pendingButtonExplanation, pendingButtonText and pendingButtonHeader should provide a consistent message about what will be done when the action is applied.
  </pending-state-fields>

  <reapply-state-fields>
    <context>Shown when Apply button is displayed - action was previously applied but has been undone</context>
    - **reapplyButtonText**: Text for Apply button (5-30 characters). Examples: "Reapply changes", "Apply again"
    - **reapplyButtonExplanation**: Description of what re-running the interaction will do (50-100 characters). Future tense. Example: "Will reapply the concise executive summary update"
    - **reapplyButtonHeader**: Short summary of what re-running the interaction will do (10-25 characters). Future tense. Example: "Reapply update"
    - reapplyButtonExplanation, reapplyButtonText and reapplyButtonHeader should provide a consistent message about what will be done when the action is re-applied.
  </reapply-state-fields>
</button-field-requirements>
${additionalLLMInstructions ? `<page-instructions>${additionalLLMInstructions}</page-instructions>` : ''}
</response-guidelines> `;

// TODO: remove this once the gate is removed from chatspot-ui
export const formatResponseInstructionsGPT5 = formatResponseInstructions;

// Data for comparing response with state
export const getMatchingDefinedFunction = (customFEActions, functionName) => {
  if (!customFEActions) {
    return null;
  }
  const currentValueOfProperty = customFEActions.find(interactionState => interactionState.name === functionName);
  if (!currentValueOfProperty) {
    Raven.captureMessage('Failed to get matching defined function based on LLM determined function name', {
      extra: {
        component: 'getMatchingDefinedFunction',
        functionName
      }
    });
    return null;
  }
  return currentValueOfProperty;
};

// Get the sate from something or other
export const getCurrentStateOfFunctionValuesByName = (customFEActions, functionName) => {
  const currentValueOfProperty = getMatchingDefinedFunction(customFEActions, functionName);
  if (!currentValueOfProperty) {
    return null;
  }
  const parameterState = Object.entries(currentValueOfProperty.functionParameters).reduce((acc, [key, value]) => {
    acc[key] = value.currentValue;
    return acc;
  }, {});
  return parameterState;
};
const deepCompareValues = (current, llm) => {
  if (current === llm) {
    return true;
  }
  if (current == null || llm == null) {
    return current === llm;
  }

  // Handle arrays
  if (Array.isArray(current) && Array.isArray(llm)) {
    if (current.length !== llm.length) {
      return false;
    }
    return current.every((item, index) => deepCompareValues(item, llm[index]));
  }

  // Handle objects
  if (typeof current === 'object' && typeof llm === 'object') {
    // Only compare the keys that exist in the LLM parameters and are not null
    const llmKeys = Object.keys(llm).filter(key => llm[key] !== null && llm[key] !== undefined);
    return llmKeys.every(key => deepCompareValues(current[key], llm[key]));
  }

  // Handle primitive values (convert to string for comparison to handle type differences)
  return String(current) === String(llm);
};
export const getMatchingParameterValues = (currentValuesOfFunction, llmDeterminedAction) => {
  if (!currentValuesOfFunction || !llmDeterminedAction) {
    return null;
  }
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);

  // Only compare keys from LLM parameters that have non-null values
  const keysToCompare = Object.keys(llmDefinedParameters).filter(key => llmDefinedParameters[key] !== null && llmDefinedParameters[key] !== undefined);

  // If no keys to compare, consider them different (safer default)
  if (keysToCompare.length === 0) {
    return false;
  }
  return keysToCompare.reduce((acc, key) => {
    var _llmValue$;
    const llmValue = llmDefinedParameters[key];
    const currentValue = currentValuesOfFunction[key];
    // If its particular item, we need to get the undo values for that item.
    if (Array.isArray(llmValue) && (_llmValue$ = llmValue[0]) !== null && _llmValue$ !== void 0 && _llmValue$.itemId) {
      acc[key] = llmValue.map(llmItem => {
        // Note: itemId is required for the items to be compared.
        const currentItem = currentValue.find(item => item.itemId === llmItem.itemId);
        // This should not happen! would be trying to update a non-existent item. (or one deleted!)
        if (!currentItem) {
          return false;
        }
        return getMatchingParameterValues(currentItem, llmItem);
      });
    } else if (!Array.isArray(llmValue) && typeof llmValue === 'object') {
      acc[key] = getMatchingParameterValues(currentValue, llmValue);
    } else {
      // Otherwise... we just use the current value (arrays, strings, numbers, etc.)
      acc[key] = currentValue;
    }
    return acc;
  }, {});
};
export const getAreAllParametersTheSame = (currentValuesOfFunction, llmDeterminedAction) => {
  if (!currentValuesOfFunction || !llmDeterminedAction) {
    return false;
  }
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);

  // Only compare keys from LLM parameters that have non-null values
  const keysToCompare = Object.keys(llmDefinedParameters).filter(key => llmDefinedParameters[key] !== null && llmDefinedParameters[key] !== undefined);

  // If no keys to compare, consider them different (safer default)
  if (keysToCompare.length === 0) {
    return false;
  }
  return keysToCompare.every(key => {
    var _llmValue$2;
    const llmValue = llmDefinedParameters[key];
    const currentValue = currentValuesOfFunction[key];
    if (Array.isArray(llmValue) && (_llmValue$2 = llmValue[0]) !== null && _llmValue$2 !== void 0 && _llmValue$2.itemId) {
      return llmValue.every(llmItem => {
        // Note: itemId is required for the items to be compared.
        const currentItem = currentValue.find(item => item.itemId === llmItem.itemId);
        // Only compare the values of the llmItem - the state may have more items than the llmItem.
        return getAreAllParametersTheSame(currentItem, llmItem);
      });
    }
    const isEqual = deepCompareValues(currentValue, llmValue);
    return isEqual;
  });
};

// If there is a mismatch, either the key was deleted from a list
// Or... something changed... the apply/undo will no work - so needs to be disabled.
export const getDoesStateHasMatchingParameters = (currentState, llmDeterminedAction) => {
  const matchingValues = getMatchingParameterValues(currentState, llmDeterminedAction);
  if (!matchingValues || !llmDeterminedAction) {
    return false;
  }
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);

  // Only compare keys from LLM parameters that have non-null values
  const keysToCompare = Object.keys(llmDefinedParameters).filter(key => llmDefinedParameters[key] !== null && llmDefinedParameters[key] !== undefined);
  return keysToCompare.every(key => {
    var _llmValue$3;
    const llmValue = llmDefinedParameters[key];
    const currentValue = matchingValues[key];
    if (Array.isArray(llmValue) && (_llmValue$3 = llmValue[0]) !== null && _llmValue$3 !== void 0 && _llmValue$3.itemId) {
      return llmValue.every(llmItem => {
        // Note: itemId is required for the items to be compared.
        const currentItem = currentValue.find(item => item.itemId === llmItem.itemId);
        // This should not happen! would be trying to update a non-existent item. (or one deleted!)
        if (!currentItem) {
          return false;
        }
        // Only compare the values of the llmItem - the state may have more items than the llmItem.
        return getDoesStateHasMatchingParameters(currentItem, llmItem);
      });
    } else if (!Array.isArray(llmValue) && typeof llmValue === 'object') {
      // Recursively check the object
      return getDoesStateHasMatchingParameters(currentValue, llmValue);
    }
    // Check that the key exists in the matchingValues object
    // Includes arrays, strings, numbers, etc.
    return Object.prototype.hasOwnProperty.call(currentState, key);
  });
};
export const shouldAutoApplyAction = (llmDeterminedActions, actionDefinition, userDefinedActions) => {
  const autoApplySetting = actionDefinition.autoApplySetting || 'llm-decides-to-auto-apply';
  const toolOutputOnly = actionDefinition.toolOutputOnly;

  // if we're forcing auto-apply, we don't need to check anything else
  // if the action is a tool output only, we don't need to check anything else
  // and we always auto apply
  if (autoApplySetting === 'always-auto-apply' || toolOutputOnly) {
    return true;
  }

  // if we're not forcing auto-apply and there are multiple interactions
  // we should not auto-apply
  const exactlyOneAction = (llmDeterminedActions === null || llmDeterminedActions === void 0 ? void 0 : llmDeterminedActions.length) === 1;
  const hasAccessToUserDefinedActions = !!userDefinedActions;
  if (!exactlyOneAction || !hasAccessToUserDefinedActions) {
    return false;
  }

  // function is set to never auto-apply
  if (autoApplySetting === 'never-auto-apply') {
    return false;
  }

  // LLM decides -- check showAsButton to get recommendation from the backend
  return !llmDeterminedActions[0].showAsButton;
};

// Helper function to filter out null/undefined values from an object
const filterNonNullValues = obj => {
  if (!obj || typeof obj !== 'object') return obj;
  const filtered = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
};
export const getButtonKey = (sessionId, action) => {
  // Note: not using messageId here because its inconsistent in arriving...
  // often comes in as copilot-message-...
  // TODO: buttonExplanation is an obnoxiously long key -- but its the only
  // item that seems to be reliably unique across multiple runs.
  // We should re-investigate using messageId instead.
  return `${sessionId}-${action.functionName}-${action.buttonExplanation}`;
};

// Execute actions immediately without showing buttons (auto-applied actions)
export const processInstantActions = ({
  actions,
  customFEActions,
  capturedStatesRef,
  addToQueue,
  sessionId,
  usageTracker,
  onSnapshotReturned,
  interactionId
}) => {
  actions.forEach(action => {
    const matchingFunction = getMatchingDefinedFunction(customFEActions.interactions, action.functionName);

    // Skip if function doesn't exist
    if (!(matchingFunction !== null && matchingFunction !== void 0 && matchingFunction.function)) {
      logUnmatchedFeInteraction(action.functionName, customFEActions.interactions);
      return;
    }
    const shouldAutoApply = shouldAutoApplyAction(actions, matchingFunction, customFEActions.interactions);
    if (!shouldAutoApply) {
      return;
    }
    const uniqueKey = getButtonKey(sessionId, action);

    // CAPTURED STATES FLOW: Save original state before changes (for undo)
    // Only capture once per function to preserve the ORIGINAL state, not intermediate states
    if (!capturedStatesRef.current[uniqueKey]) {
      const currentState = getCurrentStateOfFunctionValuesByName(customFEActions.interactions, action.functionName);

      // Store original values with only non-null values to match comparison logic
      if (currentState) {
        capturedStatesRef.current = {
          [uniqueKey]: filterNonNullValues(currentState)
        };
      }
    }

    // Extract parameters and add action to execution queue
    const parameters = getExtractedParameterNames(action);
    addToQueue({
      function: matchingFunction.function,
      name: matchingFunction.name,
      asyncTimeout: matchingFunction.asyncTimeout,
      functionParameters: Object.assign({}, parameters, interactionId !== undefined && {
        interactionId
      }),
      usageTracker,
      onResult: onSnapshotReturned ? result => {
        if (result && typeof result === 'object' && 'snapshot' in result) {
          const {
            snapshot
          } = result;
          if (typeof snapshot === 'string') {
            onSnapshotReturned(uniqueKey, snapshot);
          }
        }
      } : undefined
    });
    // Reset the captured state for this function
  });
};

// Execute action when user clicks Apply or Undo button
export const processButtonClick = (action, customFEActions, addToQueue, onSnapshotReturned, sessionId) => {
  const llmDeterminedAction = action.payload.actionToInvoke;
  const isUndo = (llmDeterminedAction === null || llmDeterminedAction === void 0 ? void 0 : llmDeterminedAction.mode) === 'undo';
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);
  const matchingFunction = getMatchingDefinedFunction(customFEActions.interactions, llmDeterminedAction.functionName);

  // Can't execute if the function doesn't exist
  if (!(matchingFunction !== null && matchingFunction !== void 0 && matchingFunction.function)) {
    logUnmatchedFeInteraction(llmDeterminedAction.functionName, customFEActions.interactions);
    return;
  }
  let func = matchingFunction.function;
  if (isUndo && matchingFunction.customUndoFunction) {
    func = matchingFunction.customUndoFunction;
  }
  const uniqueKey = sessionId && !isUndo ? getButtonKey(sessionId, llmDeterminedAction) : null;
  addToQueue({
    function: func,
    name: matchingFunction.name,
    asyncTimeout: matchingFunction.asyncTimeout,
    functionParameters: llmDefinedParameters,
    onResult: onSnapshotReturned && uniqueKey ? result => {
      if (result && typeof result === 'object' && 'snapshot' in result) {
        const {
          snapshot
        } = result;
        if (typeof snapshot === 'string') {
          onSnapshotReturned(uniqueKey, snapshot);
        }
      }
    } : undefined
  });
};